import type { Hex } from "viem";

import type { StepStatus } from "../hooks/useStepRunner.js";
import type { WalletState } from "../lib/wallet.js";
import type { BuildTxSuccessPayload, StepPayload } from "../types.js";

import { useStepRunner } from "../hooks/useStepRunner.js";
import { explorerTxUrl } from "../lib/format.js";
import styles from "../styles/StepsExecutor.module.css";

/**
 * Per-step sign + send + confirm UI. Renders one row per step in the
 * `BuildTxSuccess` payload. Lifecycle logic lives in `useStepRunner`;
 * this component is presentation-only — it asks the hook whether a
 * step is unlocked and triggers `signStep(i)` on button click.
 *
 * Each row owns:
 *   - **A button** (Firmar paso N) enabled iff every prior step is
 *     in a terminal-success state AND this one isn't past `pending`.
 *   - **Its own status badge**: pending → signing → broadcast →
 *     confirmed | error.
 *   - **Explorer links + block number** once the receipt arrives.
 *
 * Why per-step instead of "sign all at once": the wallet sequences
 * prompts anyway, but if step N-1 reverts the user can retry exactly
 * that one without re-replaying earlier successful steps.
 */
export interface StepsExecutorProps {
  readonly result: BuildTxSuccessPayload;
  readonly wallet: WalletState;
  /** Called once every step is `confirmed`. Drives the TxStatusPoller. */
  readonly onCompleted?: (hashes: readonly Hex[]) => void;
}

export function StepsExecutor({
  result,
  wallet,
  onCompleted,
}: StepsExecutorProps): React.JSX.Element {
  const { statuses, isStepUnlocked, signStep, allConfirmed, confirmedCount } =
    useStepRunner({
      result,
      wallet,
      ...(onCompleted !== undefined ? { onCompleted } : {}),
    });

  return (
    <section className={styles.card}>
      <header className={styles.header}>
        <h2>Sign &amp; send</h2>
        <span className={styles.summary}>
          {confirmedCount}/{result.steps.length} steps confirmadas
        </span>
      </header>

      <ol className={styles.steps}>
        {result.steps.map((step, i) => (
          <li
            key={`${step.__typename}:${step.tx.chainId}:${step.tx.to}:${step.tx.data.slice(0, 10)}`}
            className={styles.step}
          >
            <StepRow
              step={step}
              index={i}
              status={statuses[i]!}
              unlocked={isStepUnlocked(i)}
              onSign={() => void signStep(i)}
            />
          </li>
        ))}
      </ol>

      {allConfirmed && (
        <p className={styles.allDone}>
          ✓ Todas las transacciones confirmadas on-chain.
        </p>
      )}
    </section>
  );
}

function StepRow(props: {
  readonly step: StepPayload;
  readonly index: number;
  readonly status: StepStatus;
  readonly unlocked: boolean;
  readonly onSign: () => void;
}): React.JSX.Element {
  const { step, index, status, unlocked, onSign } = props;
  const kind = step.__typename === "ApproveStep" ? "approve" : "swap";
  const hash = "hash" in status ? status.hash : undefined;
  const url =
    hash !== undefined ? explorerTxUrl(step.tx.chainId, hash) : undefined;
  const buttonLabel = labelFor(status, index);
  const buttonDisabled =
    !unlocked || status.kind === "signing" || status.kind === "broadcast";

  return (
    <div className={styles.stepBody}>
      <div className={styles.stepHead}>
        <span className={styles.stepIndex}>#{index + 1}</span>
        <span className={styles.stepKind}>{kind}</span>
        <span className={styles.stepChain}>chain {step.tx.chainId}</span>
        <StatusBadge status={status} />
      </div>
      <div className={styles.stepDetail}>
        <code className={styles.stepTo}>{step.tx.to}</code>
        {hash !== undefined && (
          <div className={styles.stepHash}>
            <code>{hash}</code>
            {url !== undefined && (
              <a href={url} target="_blank" rel="noopener noreferrer">
                ↗ explorer
              </a>
            )}
            {status.kind === "confirmed" && (
              <span className={styles.stepBlock}>
                block {status.blockNumber.toString()}
              </span>
            )}
          </div>
        )}
        {status.kind === "error" && (
          <p className={styles.stepError}>{status.message}</p>
        )}
      </div>
      <button
        type="button"
        className={styles.stepButton}
        onClick={onSign}
        disabled={buttonDisabled}
      >
        {buttonLabel}
      </button>
    </div>
  );
}

function labelFor(status: StepStatus, index: number): string {
  switch (status.kind) {
    case "confirmed":
      return "✓ Confirmada";
    case "broadcast":
      return "Esperando confirmación…";
    case "signing":
      return "Firmando…";
    case "error":
      return `Reintentar paso ${index + 1}`;
    case "pending":
    default:
      return `Firmar paso ${index + 1}`;
  }
}

function StatusBadge({
  status,
}: {
  readonly status: StepStatus;
}): React.JSX.Element {
  switch (status.kind) {
    case "pending":
      return <span className={styles.badgePending}>pendiente</span>;
    case "signing":
      return <span className={styles.badgeSigning}>firmando…</span>;
    case "broadcast":
      return <span className={styles.badgeBroadcast}>broadcast</span>;
    case "confirmed":
      return <span className={styles.badgeSent}>confirmada</span>;
    case "error":
      return <span className={styles.badgeError}>error</span>;
  }
}
